"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-3 w-20 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative font-default text-[12px] uppercase tracking-[0.15em] text-foreground hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary px-0 py-2">
                <span className="relative inline-block">
                  {item.label}
                  <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="h-8 w-48 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="font-default text-[32px] leading-none tracking-tight text-foreground hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainIndustrialHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="350" id="main-industrial-header" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-sm border-b border-border" : "border-b border-border"}`}>
      <div className="pointer-events-none absolute left-0 top-1/2 hidden -translate-x-full -translate-y-1/2 -rotate-90 origin-right pr-3 lg:block">
        <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground whitespace-nowrap">
          Swiss Quality · Industrial Distribution
        </span>
      </div>

      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
        <div className="flex items-center">
          <Link to="/" className="flex items-center" aria-label="Swiss Solutions home">
            <WvcLogo className="h-7 w-auto text-black" />
          </Link>
        </div>

        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="22">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-1 md:gap-4">
          <Link to="#" className="hidden md:block">
            <Button className="bg-[#1a2744] text-white rounded-lg px-6 h-10 font-default text-[12px] uppercase tracking-wider hover:bg-[#1a2744]/90 transition-colors">
              Request a Quote
            </Button>
          </Link>
          <Button variant="ghost" size="icon" aria-label="Open menu" onClick={() => setMobileOpen(true)} className="md:hidden text-foreground hover:bg-accent hover:text-primary">
            <Menu className="size-5" />
          </Button>
        </div>
      </div>

      <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
        <DrawerContent className="!w-full !max-w-full bg-background text-foreground border-none">
          <div className="relative h-full w-full overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_menu_backdrop.png)] opacity-10" />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />

            <div className="relative z-10 flex h-full flex-col">
              <div className="flex h-20 items-center justify-between px-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center" aria-label="Swiss Solutions home">
                  <WvcLogo className="h-7 w-auto text-black" />
                </Link>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-accent hover:text-primary">
                    <X className="size-5" />
                  </Button>
                </DrawerClose>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-10">
                <div className="mb-8 font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Navigation
                </div>
                <MenuProvider menu_id="22">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>

              <div className="border-t border-border px-6 py-6">
                <span className="font-default text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Swiss Solutions · Premium Construction Materials
                </span>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer></SafeHydrate>
    </section>;
}